import React, { useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MDButton from 'components/MDButton';
import { AgGridTable } from 'components/tables/AgGridTable';
import dayjs from 'dayjs';

function flattenRemovedProducts(removedProducts) {
  if (!Array.isArray(removedProducts) || removedProducts.length === 0) return [];
  const rows = [];
  removedProducts.forEach((group) => {
    const products = group?.products;
    if (!Array.isArray(products)) return;
    products.forEach((p) => {
      rows.push({
        productName: p?.productName ?? '—',
        brandName: p?.brandName ?? '—',
        category: p?.category ?? '—',
        expectedQty: p?.expectedQty,
        safeQty: p?.safeQty,
        shortQty: p?.shortQty,
        updatedBy:p?.updatedBy,
        updatedDate:p?.updatedDate
      });
    });
  });
  return rows;
}

function RemovedProductsTable({ rows }) {
  const gridRef = useRef();
  
  const columnDefs = useMemo(() => [
    {
      headerName: 'Product name',
      field: 'productName',
      minWidth: 200,
      flex: 1.5,
      cellStyle: { fontWeight: 500 },
    },
    {
      headerName: 'Brand name',
      field: 'brandName',
      minWidth: 150,
      flex: 1.2,
    },
    {
      headerName: 'Category',
      field: 'category',
      minWidth: 120,
      flex: 1,
    },
    {
      headerName: 'Expected',
      field: 'expectedQty',
      minWidth: 100,
      flex: 0.8,
      cellRenderer: useCallback((props) => {
        const value = props?.value;
        const display = value != null && value !== '' ? value : '—';
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 36,
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: '#F2F3F7',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#2E2E3A',
              }}
            >
              {display}
            </Box>
          </Box>
        );
      }, []),
    },
    {
      headerName: 'Safe',
      field: 'safeQty',
      minWidth: 100,
      flex: 0.8,
      cellRenderer: useCallback((props) => {
        const value = props?.value;
        const display = value != null && value !== '' ? value : '—';
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 36,
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: '#F2F3F7',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#2E2E3A',
              }}
            >
              {display}
            </Box>
          </Box>
        );
      }, []),
    },
    {
      headerName: 'Short',
      field: 'shortQty',
      minWidth: 100,
      flex: 0.8,
      cellRenderer: useCallback((props) => {
        const value = props?.value;
        const display = value != null && value !== '' ? value : '—';
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 36,
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: '#F2F3F7',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#2E2E3A',
              }}
            >
              {display}
            </Box>
          </Box>
        );
      }, []),
    },
    {
      headerName: 'Updated By',
      field: 'updatedBy',
      minWidth: 150,
      flex: 1.2,
    },
    {
      headerName: 'Time',
      field: 'updatedDate',
      minWidth: 180,
      flex: 1.3,
      cellRenderer: useCallback((props) => {
        return props?.value ? dayjs(props?.value).format('DD/MM/YYYY hh:mm a') : '-';
      }, []),
    },
  ], []);

  const getRows = useCallback((params) => {
    params.successCallback(rows, rows.length);
  }, [rows]);

  if (!rows.length) {
    return (
      <Box
        sx={{
          py: 6,
          px: 3,
          textAlign: 'center',
          borderRadius: 3,
          border: '1px dashed #E0E0E0',
          backgroundColor: '#FAFAFA',
        }}
      >
        <Typography variant="body2" color="#656575">
          No removed products.
        </Typography>
      </Box>
    );
  }

  return (
    <AgGridTable
      col={columnDefs}
      getRows={getRows}
      style={{ height: 'min(65vh, 480px)' }}
      gridOptions={{
        headerHeight: 48,
        rowHeight: 64,
      }}
    />
  );
}

export default function DriverKitTransactionSummaryModal({ closeModal }) {
  const { modalData } = useSelector((state) => state.modal);
  const { driverRow } = modalData || {};
  const row = driverRow || {};
  const removedProductRows = useMemo(
    () => flattenRemovedProducts(row.removedProducts),
    [row.removedProducts]
  );
  console.log("row",row);
  

  return (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600} component="span" color="#2E2E3A">
          Summary
        </Typography>
        <IconButton aria-label="close" onClick={closeModal} size="small" sx={{ color: '#656575' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, px: 3, pb: 2 }}>
    
        <RemovedProductsTable rows={removedProductRows} />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          pt: 0,
          justifyContent: 'flex-end',
        }}
      >
        <MDButton
          variant="outlined"
          color="dark"
          onClick={closeModal}
          sx={{
            borderRadius: '999px',
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#2E2E3A',
            color: '#2E2E3A',
            '&:hover': {
              borderColor: '#2E2E3A',
              backgroundColor: 'rgba(46, 46, 58, 0.04)',
            },
          }}
        >
          Close
        </MDButton>
      </DialogActions>
    </>
  );
}
